<template>
  <wc-dialog
    :showDialog='showDialog'
    :title='toolbarData.title'
    :icon='toolbarData.icon'
    :iconColor='toolbarData.color'
    :loading='saving'
    @cancel='cancel'
    @save='update'
    width='600'
  >
    <v-row>
      <v-col cols='12' sm='6'>
        <v-select
          :items='mustDoOptions'
          v-model='woCopy.priority'
          label="Priority level"
          :hint='mustDoHint'
          :persistent-hint='!!mustDoHint'
        ></v-select>
      </v-col>
      <v-col cols='12' sm='3'>
        <v-select
          :items='filteredTimelines'
          v-model='woCopy.timeline'
          v-on:change="timelineChange"
          label="Timeline"
          :disabled='(woCopy.estimate_obj && woCopy.estimate_obj.is_comm) || (woCopy.project_obj && woCopy.project_obj.is_comm)'
        ></v-select>
      </v-col>
      <v-col cols='12' sm='3'>
        <v-text-field
          type='number'
          label='Time on Site'
          v-model='woCopy.tos'
          :rules='[rules.intOrEmpty]'
          hint='in minutes (estimated)'
          persistent-hint
        ></v-text-field>
      </v-col>
      <v-col cols='12' sm='6'>
        <wc-datepicker
          v-model='woCopy.apt_date'
          label='Appointment date'
          :min='todaysDate'
        ></wc-datepicker>
        <v-text-field
          class='mt-2'
          label='Call before arrival?'
          v-model='woCopy.call_mins_ahead'
          :rules='[rules.intOrEmpty]'
          clearable
          prepend-icon='mdi-phone'
          :hint='woCopy.call_mins_ahead ? "in mins (clear this value if no call required)" : "if so, how many minutes ahead?"'
          persistent-hint
        ></v-text-field>
      </v-col>
      <v-col cols='12' sm='6'>
        <wc-timepicker
          :label='woCopy.apt_end ? "Apt. window start" : "Appointment time"'
          v-model='woCopy.apt_time'
          start='7'
          end='17'
          increment='15'
          clearable
        ></wc-timepicker>
        <!-- note - start time for apt end can't currently be reactive.  miscserv build times needs to be moved to a computed within the picker -->
        <!-- MJ to change this at some point (also - let build times be able to start at increments, not just whole values.) -->
        <!-- as well, need to allow build times to accept the starting time value, not just a number (so start for apt_end can be reactive to woCopy.apt_time) -->
        <v-expand-transition>
          <wc-timepicker
            v-show='woCopy.apt_time'
            class='mt-7'
            :label='woCopy.apt_end ? "Apt. window end" : "Apt.time range?"'
            v-model='woCopy.apt_end'
            start="7"
            end='17'
            increment='15'
            clearable
            :hint='woCopy.apt_end ? "" : "If a range is needed, set this value to the end time"'
            persistent-hint
          ></wc-timepicker>
        </v-expand-transition>
      </v-col>
      <v-col cols='12'>
        <v-expand-transition>
          <v-text-field
            v-if='!!woCopy.apt_time || !!woCopy.priority'
            label='Apt/Priority notes'
            v-model='woCopy.apt_notes'
            clearable
            :hint="woCopy.apt_notes ? '' : 'additional info to crew about appointment or priority level'"
            persistent-hint
          ></v-text-field>
        </v-expand-transition>
         <v-expand-transition>
          <v-text-field
            class='mt-4'
            v-if='woCopy.call_mins_ahead'
            label='Additional calling notes?'
            v-model='woCopy.call_mins_ahead_notes'
            clearable
            :hint="woCopy.call_mins_ahead_notes ? '' : 'eg. who to call if there are options'"
            persistent-hint
          ></v-text-field>
        </v-expand-transition>
      </v-col>
      <v-col cols='12'>
        <v-textarea
          class='mt-2'
          hint='Office use only - Field crew will not see considerations'
          persistent-hint
          label='Scheduling Considerations'
          v-model='woCopy.scheduling_considerations'
          outlined
        ></v-textarea>
      </v-col>
    </v-row>
    <wc-confirm
        :showDialog='showWarning'
        saveText="Ok"
        @cancel="cancelTimelineChange"
        @confirm='showWarning = false'>
        <span class='font-weight-bold'>Since you are making this change directly in the work order you must also put the invoice on hold and say ‘add the 20$ BM8 or PO1 fee’</span><br><br>
        </wc-confirm>
  </wc-dialog>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import formRules from '@/mixins/formRules';
import wosAPI from '@/modules/wos/api/';

export default {
  name: 'scheduling-considerations-dialog',
  mixins: [formRules],
  props: {
    woCopy: {  // the calling component needs to make a copy of the current wo first
      required: true
    },
    showDialog: {
      required: true,
      type: Boolean,
      default: false
    },
  },
  data: () => ({
    saving: false,
    showWarning: false,
    mustDoOptions: [
      { value: 0, text: 'Normal' },
      { value: 1, text: 'Should do today' },
      { value: 2, text: 'Must do' }
    ],
    mustDoHint: ""
  }),
  computed: {
    toolbarData () {
      return {
        title: 'Scheduling Considerations',
        icon: 'mdi-calendar-cursor',
        color: 'primary'
      }
    },
    todaysDate () {
      return moment().format('YYYY-MM-DD').toString()
    },
    filteredTimelines() {
      let options = []
      if (this.woCopy.wo_type_obj) {
        options = _.filter(this.$store.state.misc.globalOptions.woTimelines, t => t.wo_type == this.woCopy.wo_type_obj.category)
      }
      if (this.woCopy.wo_type_obj && this.woCopy.wo_type_obj.category == 'SU' && !this.woCopy.project_obj.project_detail.can_have_bm8) {
        options = []
      }
      options.unshift({ text: "Urgent", value: "URGENT"})
      options.unshift({ text: "None", value: "" })
      return options
    }
  },
  watch: {
    'woCopy.apt_date' () {
      if (this.woCopy.apt_date) {
        this.woCopy.priority = 2
        this.mustDoHint = "Set automatically when you set an appointment date"
      }
    },
    // a wo has no concept of which route it's on... so setting the correct date automatically needs some work.
    // might be able to pass it in as a prop... but for now I'm fixing the icon of the wotile for appt to not require date.
    'woCopy.apt_time' () {
      if (this.woCopy.apt_time) {
        this.woCopy.priority = 2
        this.mustDoHint = "Set automatically when you set an appointment time"
      } else if (!this.woCopy.apt_time) {
        this.woCopy.apt_end = null
      }
    }
  },
  methods: {
    async cancelTimelineChange(){
        this.woCopy.timeline = "";
        this.showWarning = false;
    },
    async timelineChange(){
        if(this.woCopy.timeline === "BM8" || this.woCopy.timeline === "PO1"){
            this.showWarning = true;
        }
    },
    async update () {
      this.saving = true;

      const data = {
        "id": this.woCopy.id,
        "tos": this.woCopy.tos,
        "priority": this.woCopy.priority,
        "apt_time": this.woCopy.apt_time,
        "apt_date": this.woCopy.apt_date,
        "apt_end": this.woCopy.apt_end,
        "apt_notes": this.woCopy.apt_notes,
        "call_mins_ahead": this.woCopy.call_mins_ahead,
        "call_mins_ahead_notes": this.woCopy.call_mins_ahead_notes,
        "timeline": this.woCopy.timeline,
        "scheduling_considerations": this.woCopy.scheduling_considerations,
        "has_scheduling_considerations": true
      };

      const returned_wo = await wosAPI.updateWO(data);
      this.saving = false;
      this.$emit('update', returned_wo)
    },
    cancel () {
      this.$emit('cancel')
    },
  }
}
</script>